<template>
  <ChartCard data-widget-id="race-by-stage">
    <ChartHeader
      label="What is the racial distribution by stage?"
      widget-download-id="race-by-stage"
      tooltip-key="demo-survey-insights-line"
    />
    <div class="chart-and-legend-container">
      <div class="chart-container">
        <LineChart id="dsr-race-by-stage" :data="chartData" :options="chartOptions" />
      </div>
      <ChartLegend :legend-items="chartLegendItems" justify-content="flex-start" />
    </div>
    <ChartExpand :is-expanded="isExpanded" @toggle-stages="toggleStages" />
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartLegend from '@/components/insights/Chart/ChartLegend.vue';
import ChartExpand from '@/components/insights/Chart/ChartExpand.vue';
import LineChart from '@/components/insights/Chart/LineChart.vue';
import { computed, ref } from 'vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { useColors } from '@/composables/colors';
import { initTooltip } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/tooltip';
import { useTracking } from '@/stores/tracking/tracking';
import { injectUndefinedBreakdown } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/injectBreakdown';

const { raceByStage } = storeToRefs(useDemographicSurveyReport());

const {
  asian,
  black,
  hispanic_latino: hispanicLatino,
  native_american: nativeAmerican,
  two_or_more: twoOrMore,
  white,
  pacific_islander: pacificIslander,
  declined,
  unknown,
} = raceByStage.value;

const {
  grey600,
  candidateDeclined,
  candidateTwoOrMore,
  candidateHawaiian,
  candidateWhite,
  candidateHispanic,
  candidateBlack,
  candidateAsian,
  candidateNativeAm,
  white: whiteColor,
} = useColors();

const isExpanded = ref(false);

const overallTotals = computed(() => ({
  review: (nativeAmerican?.review || 0)
    + (asian?.review || 0)
    + (black?.review || 0)
    + (hispanicLatino?.review || 0)
    + (white?.review || 0)
    + (pacificIslander?.review || 0)
    + (twoOrMore?.review || 0)
    + (declined?.review || 0)
    + (unknown?.review || 0),
  screen: (nativeAmerican?.screen || 0)
    + (asian?.screen || 0)
    + (black?.screen || 0)
    + (hispanicLatino?.screen || 0)
    + (white?.screen || 0)
    + (pacificIslander?.screen || 0)
    + (twoOrMore?.screen || 0)
    + (declined?.screen || 0)
    + (unknown?.screen || 0),
  assessment: (nativeAmerican?.assessment || 0)
    + (asian?.assessment || 0)
    + (black?.assessment || 0)
    + (hispanicLatino?.assessment || 0)
    + (white?.assessment || 0)
    + (pacificIslander?.assessment || 0)
    + (twoOrMore?.assessment || 0)
    + (declined?.assessment || 0)
    + (unknown?.assessment || 0),
  offer: (nativeAmerican?.offer || 0)
    + (asian?.offer || 0)
    + (black?.offer || 0)
    + (hispanicLatino?.offer || 0)
    + (white?.offer || 0)
    + (pacificIslander?.offer || 0)
    + (twoOrMore?.offer || 0)
    + (declined?.offer || 0)
    + (unknown?.offer || 0),
  hired: (nativeAmerican?.hired || 0)
    + (asian?.hired || 0)
    + (black?.hired || 0)
    + (hispanicLatino?.hired || 0)
    + (white?.hired || 0)
    + (pacificIslander?.hired || 0)
    + (twoOrMore?.hired || 0)
    + (declined?.hired || 0)
    + (unknown?.hired || 0),
}));

const chartData = computed(() => {
  if (isExpanded.value) {
    const datasets = [
      {
        label: ['American Indian or', 'Alaskan Native'],
        data: [
          ((nativeAmerican?.review || 0) / overallTotals.value.review) * 100,
          ((nativeAmerican?.screen || 0) / overallTotals.value.screen) * 100,
          ((nativeAmerican?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((nativeAmerican?.offer || 0) / overallTotals.value.offer) * 100,
          ((nativeAmerican?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateNativeAm.value,
        borderColor: candidateNativeAm.value,
      },
      {
        label: 'Asian',
        data: [
          ((asian?.review || 0) / overallTotals.value.review) * 100,
          ((asian?.screen || 0) / overallTotals.value.screen) * 100,
          ((asian?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((asian?.offer || 0) / overallTotals.value.offer) * 100,
          ((asian?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateAsian.value,
        borderColor: candidateAsian.value,
      },
      {
        label: ['Black or African', 'American'],
        data: [
          ((black?.review || 0) / overallTotals.value.review) * 100,
          ((black?.screen || 0) / overallTotals.value.screen) * 100,
          ((black?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((black?.offer || 0) / overallTotals.value.offer) * 100,
          ((black?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateBlack.value,
        borderColor: candidateBlack.value,
      },
      {
        label: 'Hispanic or Latino',
        data: [
          ((hispanicLatino?.review || 0) / overallTotals.value.review) * 100,
          ((hispanicLatino?.screen || 0) / overallTotals.value.screen) * 100,
          ((hispanicLatino?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((hispanicLatino?.offer || 0) / overallTotals.value.offer) * 100,
          ((hispanicLatino?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateHispanic.value,
        borderColor: candidateHispanic.value,
      },
      {
        label: ['Native Hawaiian or', 'Other Pacific Islander'],
        data: [
          ((pacificIslander?.review || 0) / overallTotals.value.review) * 100,
          ((pacificIslander?.screen || 0) / overallTotals.value.screen) * 100,
          ((pacificIslander?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((pacificIslander?.offer || 0) / overallTotals.value.offer) * 100,
          ((pacificIslander?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateHawaiian.value,
        borderColor: candidateHawaiian.value,
      },
      {
        label: 'White',
        data: [
          ((white?.review || 0) / overallTotals.value.review) * 100,
          ((white?.screen || 0) / overallTotals.value.screen) * 100,
          ((white?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((white?.offer || 0) / overallTotals.value.offer) * 100,
          ((white?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateWhite.value,
        borderColor: candidateWhite.value,
      },
      {
        label: 'Two or More Races',
        data: [
          ((twoOrMore?.review || 0) / overallTotals.value.review) * 100,
          ((twoOrMore?.screen || 0) / overallTotals.value.screen) * 100,
          ((twoOrMore?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((twoOrMore?.offer || 0) / overallTotals.value.offer) * 100,
          ((twoOrMore?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateTwoOrMore.value,
        borderColor: candidateTwoOrMore.value,
      },
      {
        label: 'Undefined',
        data: [
          (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
          (((declined?.screen || 0) + (unknown?.screen || 0)) / overallTotals.value.screen) * 100,
          (((declined?.assessment || 0)
              + (unknown?.assessment || 0)) / overallTotals.value.assessment) * 100,
          (((declined?.offer || 0) + (unknown?.offer || 0)) / overallTotals.value.offer) * 100,
          (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateDeclined.value,
        borderColor: candidateDeclined.value,
      },
    ].filter((dataset) => dataset.data[0] > 0);

    return {
      labels: ['Application', 'Screening', 'Assessment', 'Offer', 'Hired'],
      datasets,
    };
  }

  const datasets = [
    {
      label: ['American Indian or', 'Alaskan Native'],
      data: [
        ((nativeAmerican?.review || 0) / overallTotals.value.review) * 100,
        ((nativeAmerican?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateNativeAm.value,
      borderColor: candidateNativeAm.value,
    },
    {
      label: 'Asian',
      data: [
        ((asian?.review || 0) / overallTotals.value.review) * 100,
        ((asian?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateAsian.value,
      borderColor: candidateAsian.value,
    },
    {
      label: ['Black or African', 'American'],
      data: [
        ((black?.review || 0) / overallTotals.value.review) * 100,
        ((black?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateBlack.value,
      borderColor: candidateBlack.value,
    },
    {
      label: 'Hispanic or Latino',
      data: [
        ((hispanicLatino?.review || 0) / overallTotals.value.review) * 100,
        ((hispanicLatino?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateHispanic.value,
      borderColor: candidateHispanic.value,
    },
    {
      label: ['Native Hawaiian or', 'Other Pacific Islander'],
      data: [
        ((pacificIslander?.review || 0) / overallTotals.value.review) * 100,
        ((pacificIslander?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateHawaiian.value,
      borderColor: candidateHawaiian.value,
    },
    {
      label: 'White',
      data: [
        ((white?.review || 0) / overallTotals.value.review) * 100,
        ((white?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateWhite.value,
      borderColor: candidateWhite.value,
    },
    {
      label: 'Two or More Races',
      data: [
        ((twoOrMore?.review || 0) / overallTotals.value.review) * 100,
        ((twoOrMore?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateTwoOrMore.value,
      borderColor: candidateTwoOrMore.value,
    },
    {
      label: 'Undefined',
      data: [
        (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
        (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateDeclined.value,
      borderColor: candidateDeclined.value,
    },
  ].filter((dataset) => dataset.data[0] > 0);

  return {
    labels: ['Application', 'Hired'],
    datasets,
  };
});

const chartLegendItems = computed(() => chartData.value.datasets.map((dataset) => ({
  label: dataset.label,
  color: dataset.backgroundColor,
})));

const suggestedMax = computed(() => {
  let max = 0;

  const hireMax = Math.max(
    ((black?.hired || 0) / overallTotals.value.hired) * 100,
    ((twoOrMore?.hired || 0) / overallTotals.value.hired) * 100,
    (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
    ((nativeAmerican?.hired || 0) / overallTotals.value.hired) * 100,
    ((asian?.hired || 0) / overallTotals.value.hired) * 100,
    ((hispanicLatino?.hired || 0) / overallTotals.value.hired) * 100,
    ((white?.hired || 0) / overallTotals.value.hired) * 100,
    ((pacificIslander?.hired || 0) / overallTotals.value.hired) * 100,
  );

  const applicationMax = Math.max(
    ((black?.review || 0) / overallTotals.value.review) * 100,
    ((twoOrMore?.review || 0) / overallTotals.value.review) * 100,
    (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
    ((nativeAmerican?.review || 0) / overallTotals.value.review) * 100,
    ((asian?.review || 0) / overallTotals.value.review) * 100,
    ((hispanicLatino?.review || 0) / overallTotals.value.review) * 100,
    ((white?.review || 0) / overallTotals.value.review) * 100,
    ((pacificIslander?.review || 0) / overallTotals.value.review) * 100,
  );

  const screenMax = Math.max(
    ((black?.screen || 0) / overallTotals.value.screen) * 100,
    ((twoOrMore?.screen || 0) / overallTotals.value.screen) * 100,
    (((declined?.screen || 0) + (unknown?.screen || 0)) / overallTotals.value.screen) * 100,
    ((nativeAmerican?.screen || 0) / overallTotals.value.screen) * 100,
    ((asian?.screen || 0) / overallTotals.value.screen) * 100,
    ((hispanicLatino?.screen || 0) / overallTotals.value.screen) * 100,
    ((white?.screen || 0) / overallTotals.value.screen) * 100,
    ((pacificIslander?.screen || 0) / overallTotals.value.screen) * 100,
  );

  const assessmentMax = Math.max(
    ((black?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((twoOrMore?.assessment || 0) / overallTotals.value.assessment) * 100,
    (((declined?.assessment || 0)
      + (unknown?.assessment || 0))
      / overallTotals.value.assessment) * 100,
    ((nativeAmerican?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((asian?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((hispanicLatino?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((white?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((pacificIslander?.assessment || 0) / overallTotals.value.assessment) * 100,
  );

  const offerMax = Math.max(
    ((black?.offer || 0) / overallTotals.value.offer) * 100,
    ((twoOrMore?.offer || 0) / overallTotals.value.offer) * 100,
    (((declined?.offer || 0) + (unknown?.offer || 0)) / overallTotals.value.offer) * 100,
    ((nativeAmerican?.offer || 0) / overallTotals.value.offer) * 100,
    ((asian?.offer || 0) / overallTotals.value.offer) * 100,
    ((hispanicLatino?.offer || 0) / overallTotals.value.offer) * 100,
    ((white?.offer || 0) / overallTotals.value.offer) * 100,
    ((pacificIslander?.offer || 0) / overallTotals.value.offer) * 100,
  );

  if (isExpanded.value) {
    max = Math.max(
      hireMax,
      applicationMax,
      screenMax,
      assessmentMax,
      offerMax,
    );
  } else {
    max = Math.max(hireMax, applicationMax);
  }

  return Math.ceil(max / 10) * 10;
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      borderWidth: 1,
    },
    point: {
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      position: 'left',
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      min: 0,
      border: {
        display: true,
        dash: [5, 5],
      },
      grid: {
        display: true,
        tickWidth: 0,
      },
      ticks: {
        callback: (value) => `${value}%`,
        color: grey600.value,
        font: {
          size: 10,
        },
        maxTicksLimit: suggestedMax.value / 10 + 1,
      },
    },
    y1: {
      position: 'right',
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      min: 0,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: grey600.value,
        font: {
          size: 13,
          weight: 400,
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: (context) => initTooltip({
        context,
        tooltipId: 'race-by-stage-tooltip',
        createLineItems: (tooltip) => {
          const { dataIndex } = tooltip?.dataPoints?.[0] || {};
          // eslint-disable-next-line no-underscore-dangle
          const { datasets } = tooltip?.chart?.config?._config?.data || {};

          if (dataIndex === undefined || !datasets) {
            return [];
          }

          const sortedDatasets = datasets
            .map((dataset) => {
              const data = dataset.data[dataIndex];
              const datasetLabel = Array.isArray(dataset.label) ? dataset.label.join(' ') : dataset.label;
              const value = `${data.toFixed(1)}%`;
              const color = dataset.backgroundColor;
              const tooltipDatasetLabel = Array.isArray(tooltip.dataPoints[0].dataset.label) ? tooltip.dataPoints[0].dataset.label.join(' ') : tooltip.dataPoints[0].dataset.label;
              const isBold = datasetLabel === tooltipDatasetLabel;

              return {
                value: `${value} ${datasetLabel}`,
                color,
                isBold,
                data,
              };
            })
            .sort((a, b) => b.data - a.data);

          const { label } = tooltip.dataPoints[0];

          const finalDatasets = injectUndefinedBreakdown(
            sortedDatasets,
            label,
            raceByStage,
            overallTotals,
            whiteColor,
          );

          return finalDatasets;
        },
        createTitle: (tooltip) => tooltip.dataPoints[0].label,
      }),
    },
  },
}));

const toggleStages = () => {
  if (!isExpanded.value) {
    useTracking().trackName({
      name: 'Expanded Race Graph on Demographic-survey-report page',
    });
  }

  isExpanded.value = !isExpanded.value;
};
</script>

<style lang="less" scoped>
.chart-and-legend-container {
  padding: 10px 16px 5px 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px;
  gap: 20px;
}

.chart-container {
  height: 280px;
  width: 100%;
}
</style>

<style lang="less">
.race-by-stage-toggle-tooltip {
  font-size: 10px !important;
  font-weight: 500 !important;
  line-height: 12px !important;
  padding: 8px !important;
}
</style>
